import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * A home page's cards, drawn: `HomeCard` draws one card from what its route answered, and
 * `HomeGrid` lays the cards out on a grid of four columns and rows of 118px, and lets the person
 * move and resize them — by pointer or by keyboard — before keeping the arrangement.
 *
 * Every figure comes from `evaluateCard` (home-model.ts), so what is drawn is what was tested; these only
 * write it — a figure, a list of bars, a table whose names link to their records, a trend — with
 * the blocks every screen draws with. The host reads the route (with the viewer's own session, after
 * checking it against the unit's /ui/api.json) and hands the outcome in; nothing here fetches.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { Cell, formatDay, numbersIn } from "./render.js";
import { Button, Menu, Notice, cx } from "./ui.js";
import { EmptyState, SkeletonLines, Table } from "./blocks.js";
import { BarList, TrendChart } from "./chart.js";
import { HOME_PRESET_SIZES, evaluateCard, keyEdit, placeCard, readPath, scriptData, sizeCard, sizeFromDrag, sizeWords, templateContext, } from "./home-model.js";
import { ShadowMarkup, readMarkup, renderTemplate } from "./home-markup.js";
import { CardScriptFrame } from "./home-script.js";
// ── Words for values ────────────────────────────────────────────────────────────────────────
const DAY = /^\d{4}-\d{2}-\d{2}$/;
const MONTH = /^\d{4}-\d{2}$/;
const MOMENT = /^\d{4}-\d{2}-\d{2}T/;
/** A figure the reader's way: grouped, at most two decimals, Western digits in both languages. */
export function homeNumber(n, i18n) {
    return n.toLocaleString(numbersIn(i18n.locale), { maximumFractionDigits: 2 });
}
/** A group's name, written: a day or a month in the reader's words, else as it came; «No value» for none. */
function keyWords(key, i18n, short = false) {
    if (key === '')
        return i18n.t('home.card.none');
    const locale = numbersIn(i18n.locale);
    if (DAY.test(key)) {
        if (!short)
            return formatDay(key, locale);
        const [y, m, d] = key.split('-').map(Number);
        return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' });
    }
    if (MONTH.test(key)) {
        const [y, m] = key.split('-').map(Number);
        return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(locale, short ? { month: 'short', timeZone: 'UTC' } : { month: 'long', year: 'numeric', timeZone: 'UTC' });
    }
    return key;
}
/** The type a table cell is written as, read off its value — the card declares fields, not types. */
function typeOf(value) {
    if (typeof value === 'number')
        return 'number';
    if (typeof value === 'string' && DAY.test(value))
        return 'date';
    if (typeof value === 'string' && MOMENT.test(value))
        return 'datetime';
    return undefined;
}
const INK = {
    neutral: 'text-foreground', blue: 'text-tone-blue', green: 'text-tone-green', amber: 'text-tone-amber', red: 'text-tone-red',
};
/** What the card will say, from its state: a reading, a refusal of access, a unit not answering, or what the answer made. */
export function cardView(card, state) {
    switch (state.state) {
        case 'loading': return { view: 'loading' };
        case 'forbidden': return { view: 'forbidden' };
        case 'unavailable':
        case 'timeout': return { view: 'unavailable' };
        case 'rejected': return state.status === 401 || state.status === 403 ? { view: 'forbidden' } : { view: 'rejected' };
        case 'ok': return { view: 'drawn', result: evaluateCard(card, state.data) };
    }
}
/**
 * One card: its title (and where it reads from), its menu, and what its route answered — a figure,
 * bars, a table or a trend. A card whose reader has no access says so and stays; a unit that is
 * not answering is said, not thrown, so one card never takes the page down.
 */
export function HomeCard({ card, state, ctx, source, editing = false, onEditAi, onEditFields, onResize, onCopy, onRemove, onRetry, missing, forms, onForm, onRead, onScriptAction, className, ...rest }) {
    const i18n = ctx.i18n;
    const title = card.title[i18n.locale] || card.title.en;
    const shown = cardView(card, state);
    const drawn = shown.view === 'drawn' ? shown.result : undefined;
    const items = [];
    if (onEditAi)
        items.push({ key: 'edit_ai', label: i18n.t('home.card.edit_ai'), onPick: onEditAi, attrs: { 'data-home-action': 'edit_ai' } });
    if (onEditFields)
        items.push({ key: 'edit_fields', label: i18n.t('home.card.edit_fields'), onPick: onEditFields, attrs: { 'data-home-action': 'edit_fields' } });
    if (onResize) {
        for (const size of HOME_PRESET_SIZES) {
            const words = sizeWords(size);
            items.push({
                key: `size_${words}`, label: i18n.t('home.card.size_to', { size: words }), onPick: () => onResize(size),
                disabled: size.w === card.size.w && size.h === card.size.h, attrs: { 'data-home-action': 'size', 'data-home-size': words },
            });
        }
    }
    if (onCopy)
        items.push({ key: 'copy', label: i18n.t('home.card.copy'), onPick: onCopy, attrs: { 'data-home-action': 'copy' } });
    if (onRemove) {
        if (items.length)
            items.push('divider');
        items.push({ key: 'remove', label: i18n.t('home.card.remove'), onPick: onRemove, destructive: true, attrs: { 'data-home-action': 'remove' } });
    }
    const body = (() => {
        switch (shown.view) {
            case 'loading': return _jsx(SkeletonLines, { label: i18n.t('state.loading') });
            case 'forbidden': return _jsx(Notice, { tone: "red", "data-home-state-note": "forbidden", children: i18n.t('home.card.forbidden') });
            case 'unavailable':
                return _jsx(Notice, { tone: "neutral", "data-home-state-note": "unavailable", action: onRetry ? _jsx(Button, { size: "sm", "data-home-retry": "", onClick: onRetry, children: i18n.t('action.retry') }) : undefined, children: i18n.t('home.card.unavailable') });
            case 'rejected': return _jsx(Notice, { tone: "amber", "data-home-state-note": "rejected", children: i18n.t('home.refused.rejected') });
            case 'drawn': {
                const offers = onForm ? (forms ?? []) : [];
                const press = (target, row) => onForm?.(target, row);
                if (card.script && state.state === 'ok' && shown.result.kind !== 'refused') {
                    const data = scriptData(card, state.data);
                    // `data` below is made anew at every render; the frame posts it only when its content changed.
                    return (_jsx(CardScriptFrame, { card: card, dir: i18n.dir, lang: i18n.locale, title: i18n.t('home.script.frame', { title }), maxHeight: Math.max(40, card.size.h * 118 + (card.size.h - 1) * 12 - 58), data: { title, locale: i18n.locale, shape: card.shape, rows: data.rows, total: data.total, paged: data.paged, result: data.result }, ...(onRead ? { onRead } : {}), ...(onScriptAction ? { onAction: onScriptAction } : {}) }));
                }
                return _jsx(CardBody, { card: card, result: shown.result, ctx: ctx, title: title, missing: missing ?? [], offers: offers, press: press });
            }
        }
    })();
    const cardOffers = shown.view === 'drawn' && onForm && !card.script
        ? (forms ?? []).filter((f) => !f.row && !(card.template ?? '').includes(`{{action ${f.target}}}`)) : [];
    const status = shown.view === 'drawn' ? (drawn.kind === 'refused' || drawn.kind === 'empty' ? drawn.kind : 'ok') : shown.view;
    return (_jsxs("section", { "data-home-card": card.id, "data-home-shape": card.shape, "data-home-state": status, "aria-label": title, ...rest, className: cx('flex h-full min-h-0 min-w-0 flex-col gap-2 overflow-hidden rounded-xl border bg-card px-3.5 py-3 text-card-foreground shadow-card', editing && 'border-dashed border-brand/45 pt-9', className), children: [_jsxs("div", { className: "flex min-w-0 items-center justify-between gap-2", children: [_jsxs("h3", { dir: "auto", className: "m-0 min-w-0 truncate text-[13.5px] font-semibold", title: title, "data-home-title": "", children: [title, source ? _jsx("span", { className: "ms-2 text-[11.5px] font-normal text-muted-foreground", "data-home-source": "", children: source }) : null] }), items.length ? _jsx(Menu, { trigger: "\u22EF", label: i18n.t('home.card.menu'), items: items, "data-home-menu": "" }) : null] }), _jsx("div", { className: "flex min-h-0 flex-1 flex-col gap-1.5", "data-home-body": "", children: body }), cardOffers.length ? (_jsx("div", { className: "flex flex-wrap gap-1.5", "data-home-forms": "", children: cardOffers.map((f) => _jsx(Button, { size: "sm", "data-home-form": f.target, onClick: () => onForm(f.target), children: f.label }, f.target)) })) : null] }));
}
/** A value as a template writes it: a figure grouped, a day in the reader's calendar, «—» for nothing. */
export function markupValue(value, i18n) {
    if (value === null || value === undefined || value === '')
        return '—';
    if (typeof value === 'number')
        return Number.isFinite(value) ? homeNumber(value, i18n) : '—';
    if (typeof value === 'boolean')
        return i18n.t(value ? 'home.card.yes' : 'home.card.no');
    if (typeof value === 'string') {
        if (DAY.test(value))
            return formatDay(value, numbersIn(i18n.locale));
        if (MOMENT.test(value))
            return formatDay(value.slice(0, 10), numbersIn(i18n.locale));
        return value;
    }
    return '—';
}
function CardBody({ card, result, ctx, title, missing, offers, press }) {
    const i18n = ctx.i18n;
    const more = (n) => (n > 0 ? _jsx("p", { className: "m-0 text-[12px] text-muted-foreground", "data-home-more": n, children: i18n.t('home.card.more', { n: homeNumber(n, i18n) }) }) : null);
    // The card's own markup, when it has some and it reads clean; a card that would lose anything to
    // the reader is not drawn half — the stock shape is, and the card says why.
    const markup = card.template || card.style ? readMarkup(card) : null;
    if (markup && markup.stripped.length === 0 && markup.nodes.length && result.kind !== 'refused' && result.kind !== 'empty') {
        const offer = (target, row) => {
            const f = offers.find((o) => o.target === target && o.row === Boolean(row));
            if (!f || (row && f.applies && !f.applies(row)))
                return null;
            return _jsx("button", { type: "button", "data-card-action": target, onClick: () => press(target, row), children: f.label });
        };
        return (_jsx(ShadowMarkup, { css: markup.css, dir: i18n.dir, className: "min-h-0 flex-1 overflow-hidden", children: renderTemplate(markup.nodes, { context: { ...templateContext(result), title }, format: (v) => markupValue(v, i18n), action: offer }) }));
    }
    const refusedMarkup = markup && markup.stripped.length
        ? _jsx(Notice, { tone: "amber", "data-home-markup-refused": markup.stripped.map((x) => x.why).join(' '), children: i18n.t('home.refused.markup') }) : null;
    const rowOffers = offers.filter((o) => o.row);
    const drawn = (() => {
        switch (result.kind) {
            case 'refused':
                return _jsx(Notice, { tone: "amber", "data-home-refused": result.reason, children: i18n.t(`home.refused.${result.reason}`, result.field ? { field: result.field } : undefined) });
            case 'empty':
                return _jsx("p", { className: "m-0 text-[13px] text-ink-secondary", "data-home-empty": "", children: i18n.t('home.card.empty') });
            case 'number':
                // The card is already the tile, so the figure is written as `Figure` writes its own, without a second frame around it.
                return (_jsx("p", { className: cx('m-0 text-[30px] font-bold leading-none tabular-nums', INK[card.tone ?? 'neutral']), "data-home-figure": result.basis, children: _jsx("bdi", { children: homeNumber(result.value, i18n) }) }));
            case 'bars':
                return (_jsxs(_Fragment, { children: [_jsx(BarList, { tone: card.tone ?? 'blue', caption: title, items: result.items.map((b) => ({ key: b.key, label: keyWords(b.key, i18n), title: keyWords(b.key, i18n), value: b.value, text: homeNumber(b.value, i18n) })) }), more(result.more)] }));
            case 'trend': {
                // Drawn at a width that keeps the card's proportions, so the line fills it rather than a strip of it.
                const across = card.size.w * 262;
                const high = Math.max(60, card.size.h * 130 - 60);
                const width = Math.round(Math.min(1000, Math.max(480, (230 * across) / high)));
                return (_jsx(TrendChart, { tone: card.tone ?? 'blue', caption: title, rtl: i18n.dir === 'rtl', width: width, format: (n) => homeNumber(n, i18n), className: "min-h-0 flex-1 [&_svg]:h-full [&_svg]:w-full", points: result.points.map((p) => ({ key: p.key, label: keyWords(p.key, i18n, true), title: keyWords(p.key, i18n), value: p.value, text: homeNumber(p.value, i18n) })) }));
            }
            case 'table': {
                const columns = result.columns;
                return (_jsx(Table, { "data-home-table": "", columns: [...columns.map((c) => ({ key: c.field, label: c.label[i18n.locale] || c.label.en })),
                        ...(rowOffers.length ? [{ key: '__forms', label: '' }] : [])], rows: result.rows.map((row, i) => {
                        // Each field read by its path, and a `ref`'s id beside it, so a name links to its record as it does in a list.
                        const flat = {};
                        for (const c of columns) {
                            flat[c.field] = readPath(row, c.field);
                            if (c.ref?.id)
                                flat[c.ref.id] = readPath(row, c.ref.id);
                        }
                        const id = readPath(row, 'id');
                        return {
                            key: typeof id === 'string' || typeof id === 'number' ? `${id}` : `r${i}`,
                            cells: Object.fromEntries([...(rowOffers.length ? [['__forms', (_jsx("span", { className: "flex flex-wrap justify-end gap-1", "data-home-row-forms": "", children: rowOffers.filter((f) => !f.applies || f.applies(row)).map((f) => (_jsx(Button, { size: "sm", variant: "quiet", "data-home-form": f.target, onClick: () => press(f.target, row), children: f.label }, f.target))) }))]] : []), ...columns.map((c) => {
                                    if (missing.includes(c.field))
                                        return [c.field, _jsx("span", { className: "text-tone-red", "data-home-missing": c.field, children: i18n.t('home.card.missing') })];
                                    const type = typeOf(flat[c.field]);
                                    const column = { key: c.field, label: '', ...(type ? { type } : {}), ...(c.ref ? { ref: { entity: c.ref.entity, ...(c.ref.id ? { id: c.ref.id } : {}) } } : {}) };
                                    return [c.field, _jsx(Cell, { value: flat[c.field], column: column, ctx: ctx, row: flat })];
                                })]),
                        };
                    }), footer: result.more > 0 ? i18n.t('home.card.more', { n: homeNumber(result.more, i18n) }) : undefined }));
            }
        }
    })();
    return refusedMarkup ? _jsxs(_Fragment, { children: [refusedMarkup, drawn] }) : drawn;
}
// ── HomeGrid ────────────────────────────────────────────────────────────────────────────────
/** Spans written out whole, so the stylesheet's scanner sees each: one column on a phone, the card's width from `md` up. */
const SPAN_W = { 1: 'md:col-span-1', 2: 'md:col-span-2', 3: 'md:col-span-3', 4: 'md:col-span-4' };
const SPAN_H = { 1: 'row-span-1', 2: 'row-span-2', 3: 'row-span-3' };
/** The empty page's mark: a grid of squares, one of them a plus. */
function GridMark() {
    return (_jsxs("svg", { viewBox: "0 0 24 24", width: "22", height: "22", fill: "none", stroke: "currentColor", strokeWidth: "1.8", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": true, children: [_jsx("rect", { x: "3", y: "3", width: "7", height: "7", rx: "1.5" }), _jsx("rect", { x: "14", y: "3", width: "7", height: "7", rx: "1.5" }), _jsx("rect", { x: "3", y: "14", width: "7", height: "7", rx: "1.5" }), _jsx("path", { d: "M17.5 14v7M14 17.5h7" })] }));
}
/**
 * The page's cards on four columns (one on a phone, in the same order), rows of 118px, 12px apart.
 * Arranging: each card is dashed, says its size (`2×1`) and has a corner to drag. Dragging a card
 * over another puts it there; dragging the corner resizes it by whole columns and rows. From the
 * keyboard, a focused card moves with the arrows and resizes with Shift and the arrows (`keyEdit`).
 * Nothing is kept until «Done».
 */
export function HomeGrid({ cards, i18n, editing = false, onChange, onCancel, renderCard, emptyAction, className, ...rest }) {
    const [draft, setDraft] = useState(() => (editing ? cards : null));
    const entry = useRef(cards);
    const grid = useRef(null);
    const drag = useRef(null);
    // The card the keyboard just moved: its element changed place, and a browser may drop focus from a moved element.
    const keep = useRef(null);
    const hint = useId();
    const rtl = i18n.dir === 'rtl';
    useEffect(() => {
        if (editing) {
            entry.current = cards;
            setDraft(cards);
        }
        else
            setDraft(null);
    }, [editing]);
    useEffect(() => {
        const id = keep.current;
        keep.current = null;
        if (!id)
            return;
        const el = grid.current?.querySelector(`[data-home-cell="${CSS.escape(id)}"]`);
        if (el && document.activeElement !== el)
            el.focus();
    }, [draft]);
    const shown = editing && draft ? draft : cards;
    if (!shown.length && !editing) {
        return (_jsx(EmptyState, { "data-home-empty-page": "", icon: _jsx(GridMark, {}), title: i18n.t('home.grid.empty.title'), description: i18n.t('home.grid.empty.lede'), action: emptyAction, className: cx('border-dashed', className), ...rest }));
    }
    const begin = (e, card, kind) => {
        if (!editing || e.button !== 0)
            return;
        const target = e.target;
        if (kind === 'move') {
            // A card's own controls stay its own; and on a touch screen only the size chip drags, so the page still scrolls under a finger.
            if (target.closest('button, a, input, [data-menu]'))
                return;
            if (e.pointerType === 'touch' && !target.closest('[data-home-grip]'))
                return;
        }
        e.stopPropagation();
        e.currentTarget.setPointerCapture?.(e.pointerId);
        drag.current = { id: card.id, kind, x: e.clientX, y: e.clientY, start: card.size, moved: false };
    };
    const follow = (e) => {
        const d = drag.current;
        if (!d)
            return;
        const dx = e.clientX - d.x;
        const dy = e.clientY - d.y;
        if (!d.moved && Math.hypot(dx, dy) < 4)
            return;
        d.moved = true;
        if (d.kind === 'resize') {
            const width = grid.current?.getBoundingClientRect().width ?? 0;
            setDraft((was) => (was ? sizeCard(was, d.id, sizeFromDrag(d.start, dx, dy, width, rtl)) : was));
            return;
        }
        const over = document.elementFromPoint(e.clientX, e.clientY)?.closest('[data-home-cell]')?.dataset['homeCell'];
        if (!over || over === d.id)
            return;
        setDraft((was) => (was ? placeCard(was, d.id, was.findIndex((c) => c.id === over)) : was));
    };
    const end = () => { drag.current = null; };
    const onKey = (e, card) => {
        if (!editing || e.target !== e.currentTarget)
            return;
        const next = keyEdit(draft ?? cards, card.id, e.key, e.shiftKey, rtl);
        if (next === null)
            return;
        e.preventDefault();
        keep.current = card.id;
        setDraft(next);
    };
    return (_jsxs("div", { "data-home-grid": "", "data-editing": editing ? '' : undefined, className: cx('grid gap-3', className), ...rest, children: [editing ? (_jsxs("div", { className: "flex flex-wrap items-center justify-between gap-2.5 rounded-lg border border-brand/30 bg-brand/10 px-3 py-2 text-[13px]", "data-home-editbar": "", children: [_jsx("span", { dir: "auto", id: hint, children: i18n.t('home.grid.edit.hint') }), _jsxs("span", { className: "flex gap-1.5", children: [_jsx(Button, { size: "sm", "data-home-undo": "", onClick: () => { setDraft(entry.current); onCancel?.(); }, children: i18n.t('home.grid.edit.undo') }), _jsx(Button, { size: "sm", variant: "primary", "data-home-done": "", onClick: () => onChange?.(draft ?? cards), children: i18n.t('home.grid.edit.done') })] })] })) : null, _jsx("div", { ref: grid, role: "list", "aria-label": i18n.t('home.grid.label'), className: "grid grid-cols-1 auto-rows-[118px] gap-3 md:grid-cols-4", "data-home-cells": "", children: shown.map((card) => {
                    const size = sizeWords(card.size);
                    const title = card.title[i18n.locale] || card.title.en;
                    return (_jsxs("div", { role: "listitem", "data-home-cell": card.id, "data-home-size": size, className: cx('relative min-w-0', SPAN_W[card.size.w] ?? SPAN_W[1], SPAN_H[card.size.h] ?? SPAN_H[1], editing && 'cursor-grab rounded-xl outline-offset-2 focus-visible:outline-2 focus-visible:outline-ring'), ...(editing ? { tabIndex: 0, 'aria-label': i18n.t('home.grid.cell', { title, size }), 'aria-describedby': hint } : {}), onKeyDown: (e) => onKey(e, card), onPointerDown: (e) => begin(e, card, 'move'), onPointerMove: follow, onPointerUp: end, onPointerCancel: end, children: [renderCard(card, { editing, resize: (to) => setDraft((was) => (was ? sizeCard(was, card.id, to) : was)) }), editing ? (_jsxs(_Fragment, { children: [_jsx("span", { "data-home-grip": "", dir: "ltr", "aria-hidden": true, className: "absolute end-2.5 top-2.5 touch-none rounded-md border bg-muted px-1.5 font-mono text-[11px] font-semibold tabular-nums text-ink-secondary", children: size }), _jsx("span", { "data-home-handle": "", "aria-hidden": true, title: i18n.t('home.grid.handle', { title }), onPointerDown: (e) => begin(e, card, 'resize'), className: cx('absolute bottom-1.5 end-1.5 size-3.5 touch-none rounded-ee-sm border-b-2 border-e-2 border-brand', rtl ? 'cursor-nesw-resize' : 'cursor-nwse-resize') })] })) : null] }, card.id));
                }) })] }));
}
//# sourceMappingURL=home.js.map